import { useEffect, useMemo, useRef } from "react";
import type { ChangeEvent } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Flex, Input, Menu, Typography } from "antd";

import subEntityStyles from "../../styles/SubEntityLayoutSidebar.module.scss";

import { useDefinition } from "#hooks";
import { LinkProvider } from "#providers";
import { useEntityTabsStore, useFiltersStore } from "#store";

import styles from "./Settings.module.scss";

const formatEntityLabel = (name: string) =>
	name
		.split("_")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");

/**
 * Settings Module - Admin interface for managing all entities
 *
 * Architecture:
 * - Left sidebar: Filterable list of all entities (router Links)
 * - Main content: Outlet renders SettingsEntityView (FlexTabs CRUD) for the selected entity
 * - URL-driven: /settings/$entityName — persistent, bookmarkable, back/forward works
 */
const SETTINGS_SCOPE = "settings";
const SEARCH_KEY = "search";

export const Settings = () => {
	const definition = useDefinition();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const entities = useMemo(() => {
		const definitionData = definition.data as
			| Record<string, { ui?: { bundle?: string[] } }>
			| undefined;
		if (!definitionData) return [];
		return Object.entries(definitionData)
			.filter(([, def]) => (def?.ui?.bundle ?? []).includes("manage"))
			.map(([entity]) => entity)
			.sort((a, b) => a.localeCompare(b));
	}, [definition.data]);

	const search =
		useFiltersStore(
			(state) => state.filtersByScope[SETTINGS_SCOPE]?.[SEARCH_KEY]
		) ?? "";
	const updateFilter = useFiltersStore((state) => state.updateFilter);

	const getActiveSubTab = useEntityTabsStore(
		(state) => state.getActiveSubTab
	);
	const setActiveSubTab = useEntityTabsStore(
		(state) => state.setActiveSubTab
	);

	const activeEntity = useMemo(() => {
		const match = pathname.match(/^\/settings\/(.+?)(?:\/|$)/);
		return match?.[1] ?? "";
	}, [pathname]);

	// Persist active entity to store whenever it changes
	useEffect(() => {
		if (activeEntity) {
			setActiveSubTab("settings", activeEntity);
		}
	}, [activeEntity, setActiveSubTab]);

	// Redirect /settings to last active entity (or first entity as fallback)
	useEffect(() => {
		if (pathname === "/settings" && entities.length > 0) {
			const lastActive = getActiveSubTab("settings");
			const target =
				lastActive && entities.includes(lastActive)
					? lastActive
					: entities[0];
			void navigate({ to: `/settings/${target}` as "/" });
		}
	}, [pathname, entities, navigate, getActiveSubTab]);

	const filteredEntities = useMemo(() => {
		if (!search) return entities;
		const lower = search.toLowerCase();
		return entities.filter(
			(e) =>
				e.toLowerCase().includes(lower) ||
				formatEntityLabel(e).toLowerCase().includes(lower)
		);
	}, [entities, search]);

	const menuItems = useMemo(
		() =>
			filteredEntities.map((entity) => ({
				key: entity,
				label: (
					<Link to={`/settings/${entity}` as "/"}>
						{formatEntityLabel(entity)}
					</Link>
				),
			})),
		[filteredEntities]
	);

	const menuWrapRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!activeEntity || !filteredEntities.includes(activeEntity)) {
			return;
		}

		const frameID = requestAnimationFrame(() => {
			const selectedItem = menuWrapRef.current?.querySelector<HTMLElement>(
				".ant-menu-item-selected"
			);
			selectedItem?.scrollIntoView({ block: "nearest" });
		});

		return () => cancelAnimationFrame(frameID);
	}, [activeEntity, filteredEntities]);

	return (
		<Flex vertical className={subEntityStyles.container}>
			<Flex flex={1} className={subEntityStyles.mainContent}>
				<div className={subEntityStyles.leftNav}>
					<Flex vertical className={styles.leftNavInner}>
						<Flex vertical className={styles.sidebarHeader}>
							<Typography.Text strong>Entities</Typography.Text>
							<Input
								prefix={<SearchOutlined />}
								placeholder="Search entities..."
								allowClear
								value={search}
								onChange={(
									e: ChangeEvent<HTMLInputElement>
								) => {
									updateFilter(
										SETTINGS_SCOPE,
										SEARCH_KEY,
										e.target.value
									);
								}}
								size="small"
							/>
						</Flex>
						<div ref={menuWrapRef} className={styles.menuWrap}>
							<Menu
								mode="vertical"
								selectedKeys={
									activeEntity ? [activeEntity] : []
								}
								items={menuItems}
							/>
						</div>
					</Flex>
				</div>
				<Flex flex={1} vertical className={subEntityStyles.content}>
					<LinkProvider parent="settings" parentID={activeEntity}>
						<Outlet />
					</LinkProvider>
				</Flex>
			</Flex>
		</Flex>
	);
};
